import { useQuery } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { api } from "../api";
import { useSession } from "../auth/session";

/**
 * The header's company switcher (ALN-25). One company = one QuickBooks file; "All companies" is
 * null. The choice is per browser and per tenant, and screens read it with useCompanyScope(). The
 * server still decides what each person may see (memberships.company_ids, RLS).
 */
export interface CompanyOption { id: string; name: string }
const listeners = new Set<() => void>();
const key = (tenantId: string) => `vintner.company.${tenantId}`;

function read(tenantId: string | undefined): string | null {
  if (!tenantId) return null;
  try { return localStorage.getItem(key(tenantId)); } catch { return null; }
}

export function setCompanyScope(tenantId: string, companyId: string | null): void {
  try {
    if (companyId) localStorage.setItem(key(tenantId), companyId);
    else localStorage.removeItem(key(tenantId));
  } catch { /* private mode */ }
  listeners.forEach(l => l());
}

export function useCompanies() {
  return useQuery({ queryKey: ["companies"], queryFn: () => api<{ companies: CompanyOption[] }>("/companies") });
}

/** The selected company, or null for all; a stale choice (company no longer visible) reads as all. */
export function useCompanyScope(): { companyId: string | null; companies: CompanyOption[]; tenantId: string | undefined } {
  const tenantId = useSession().data?.tenant?.id;
  const { data } = useCompanies();
  const stored = useSyncExternalStore(cb => { listeners.add(cb); return () => listeners.delete(cb); }, () => read(tenantId), () => null);
  const companies = data?.companies ?? [];
  const companyId = stored && companies.some(c => c.id === stored) ? stored : null;
  return { companyId, companies, tenantId };
}
